import type {
  FlowConfig,
  FlowScreenConfig,
  FlowScreenRouteConfig,
  FlowScreenRouteLogic,
  FlowValues,
} from './types';

type RatingValue = {
  feedback?: { tags?: string[] };
  score?: number;
  sentiment?: string;
};

export function firstRatingValue(values: FlowValues) {
  return Object.values(values).find((value) => {
    if (!value || typeof value !== 'object') return false;

    const maybeRating = value as RatingValue;
    return typeof maybeRating.score === 'number' || typeof maybeRating.sentiment === 'string';
  }) as RatingValue | undefined;
}

export function fieldValueForRoute(
  field: FlowScreenRouteLogic['field'],
  values: FlowValues,
  routeContext: FlowConfig['routeContext']
) {
  const ratingValue = firstRatingValue(values);

  if (field === 'score') return ratingValue?.score;
  if (field === 'sentiment') return ratingValue?.sentiment;
  if (field === 'selected_option') {
    return Object.values(values).flatMap((value) => {
      if (typeof value === 'string') return [value];
      if (Array.isArray(value)) {
        return value.filter((option): option is string => typeof option === 'string');
      }
      if (!value || typeof value !== 'object') return [];

      const candidate = value as {
        selectedOption?: unknown;
        option?: unknown;
        value?: unknown;
      };
      return [candidate.selectedOption, candidate.option, candidate.value].filter(
        (option): option is string => typeof option === 'string'
      );
    });
  }
  if (field === 'comment') {
    return Object.values(values)
      .flatMap((value) => {
        if (!value || typeof value !== 'object') return [];

        const candidate = value as {
          comment?: unknown;
          feedback?: { notes?: unknown };
          notes?: unknown;
          text?: unknown;
        };
        return [
          candidate.comment,
          candidate.notes,
          candidate.text,
          candidate.feedback?.notes,
        ].filter((comment): comment is string => typeof comment === 'string');
      })
      .join('\n');
  }
  if (field === 'service_type') return routeContext?.service_type;
  if (field === 'channel') return routeContext?.channel;
  if (
    field === 'has_existing_response' ||
    field === 'rating_enabled' ||
    field === 'response_editing_enabled' ||
    field === 'score_method' ||
    field === 'tipping_enabled'
  ) {
    return routeContext?.[field];
  }
  if (field === 'tag') {
    return Object.values(values).flatMap((value) => {
      if (!value || typeof value !== 'object') return [];

      const candidate = value as {
        feedback?: { tags?: unknown };
        tags?: unknown;
      };
      const tags = candidate.tags ?? candidate.feedback?.tags;
      return Array.isArray(tags)
        ? tags.filter((tag): tag is string => typeof tag === 'string')
        : [];
    });
  }

  return undefined;
}

export function compareRouteValues(candidateValue: unknown, logic: FlowScreenRouteLogic) {
  const expectedValue = logic.value ?? '';

  if (logic.operator === 'exists') {
    if (Array.isArray(candidateValue)) return candidateValue.length > 0;
    return candidateValue !== undefined && candidateValue !== null && candidateValue !== '';
  }

  if (Array.isArray(candidateValue)) {
    if (logic.operator === 'contains') return candidateValue.includes(expectedValue);
    if (logic.operator === 'equals') return candidateValue.some((value) => value === expectedValue);
    if (logic.operator === 'not_equals') return !candidateValue.includes(expectedValue);
    return false;
  }

  const numericCandidate =
    typeof candidateValue === 'number' ? candidateValue : Number(candidateValue);
  const numericExpected = Number(expectedValue);
  const canCompareNumbers = Number.isFinite(numericCandidate) && Number.isFinite(numericExpected);

  if (canCompareNumbers) {
    if (logic.operator === 'greater_than') return numericCandidate > numericExpected;
    if (logic.operator === 'greater_than_or_equal') return numericCandidate >= numericExpected;
    if (logic.operator === 'less_than') return numericCandidate < numericExpected;
  }

  const candidateText = String(candidateValue ?? '');

  if (logic.operator === 'contains') return candidateText.includes(expectedValue);
  if (logic.operator === 'equals') return candidateText === expectedValue;
  if (logic.operator === 'not_equals') return candidateText !== expectedValue;

  return false;
}

export function routeMatches(
  route: FlowScreenRouteConfig,
  values: FlowValues,
  routeContext: FlowConfig['routeContext']
) {
  if (route.logic.mode === 'always') return true;
  if (route.logic.mode === 'fallback') return false;
  if (route.logic.field === 'custom') return false;

  return compareRouteValues(
    fieldValueForRoute(route.logic.field, values, routeContext),
    route.logic
  );
}

export function resolveNextScreenIndex({
  activeScreen,
  activeScreenIndex,
  routeContext,
  screenIndexById,
  screens,
  values,
}: {
  activeScreen: FlowScreenConfig;
  activeScreenIndex: number;
  routeContext: FlowConfig['routeContext'];
  screenIndexById: Map<string, number>;
  screens: FlowScreenConfig[];
  values: FlowValues;
}) {
  if (activeScreen.routes !== undefined) {
    if (activeScreen.routes.length === 0) return null;

    const matchedRoute =
      activeScreen.routes.find((route) => routeMatches(route, values, routeContext)) ??
      activeScreen.routes.find((route) => route.logic.mode === 'fallback');

    if (!matchedRoute) return null;

    return screenIndexById.get(matchedRoute.targetScreenId) ?? null;
  }

  if (activeScreenIndex >= screens.length - 1) return null;

  return activeScreenIndex + 1;
}
